export type Money = {
  currency: string
  amount: number
} | null

export const getFormattedMoney = <TMoney extends Money>(
  money: TMoney | undefined | null,
  negative = false,
  locale = 'en-AU',
) => {
  if (!money) {
    return ''
  }

  const { amount, currency } = money

  if (amount === 0 && !negative) return 'Free'

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
  }).format(negative ? -amount : amount)
}

/**
 * Format a backend price as USD. The backend sends prices as decimal strings
 * (e.g. "79.00"); whole-dollar amounts drop the cents ("$79"), others keep
 * them ("$14.95"). Always en-US so the symbol is "$" rather than "US$".
 */
export const formatUsd = (
  amount: string | number | null | undefined,
  { freeLabel = false }: { freeLabel?: boolean } = {},
): string => {
  if (amount === null || amount === undefined || amount === '') return ''

  const parsed = typeof amount === 'number' ? amount : parseFloat(amount)

  if (Number.isNaN(parsed)) return ''

  if (parsed === 0 && freeLabel) return 'Free'

  const isWhole = Number.isInteger(parsed)

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(parsed)
}

export const getFormattedPercentage = (percentage: number, locale = 'en-AU') => {
  if (percentage === 1) {
    return ''
  }

  const option: Intl.NumberFormatOptions = {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }

  return new Intl.NumberFormat(locale, option).format(percentage)
}